import { useState, useEffect } from 'preact/hooks'
import { SessionCard } from './components/SessionCard'
import { ViewSelector } from './components/ViewSelector'
import { AppGuide } from './components/WelcomeNotice'
import { EmptyState } from './components/EmptyState'
import { Toast } from './components/Toast'
import { CategoryFilter } from './components/CategoryFilter'
import { FilterTabs } from './components/FilterTabs'
import { ExportButton } from './components/ExportButton'
import { LiveDashboard } from './components/LiveDashboard'
import { SpeakerModal } from './components/SpeakerModal'
import { DebugPanel } from './components/DebugPanel'
import { Footer } from './components/Footer'
import { TimelineView } from './components/TimelineView'
import { VisualTimelineView } from './components/VisualTimelineView'
import { MobileEnhancements } from './components/MobileEnhancements'
import { HistoricalContext } from './components/HistoricalContext'
import { formatTime } from './utils/dateUtils'
import { getStarredSessions, saveStarredSessions } from './utils/storage'
import { findConflicts, generateConflictMessage } from './utils/conflictUtils'
import analytics, { trackPageView } from './utils/analytics'

export function App() {
  const [sessions, setSessions] = useState([])
  const [starredSessions, setStarredSessions] = useState([])
  const [currentView, setCurrentView] = useState('agenda')
  const [lastMainView, setLastMainView] = useState('agenda') // Track last non-My Plan view
  const [expandedId, setExpandedId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState('All')
  const [activeContentTypes, setActiveContentTypes] = useState([])
  const [toast, setToast] = useState(null)
  const [selectedSpeaker, setSelectedSpeaker] = useState(null)
  const [showSpeakerModal, setShowSpeakerModal] = useState(false)
  const [showDebugPanel, setShowDebugPanel] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [isSimulating, setIsSimulating] = useState(false)
  const [simulatedTime, setSimulatedTime] = useState(new Date())

  // Check if dev mode is enabled
  const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'hacker'

  // Get effective time (simulated or real)
  const effectiveTime = isSimulating ? simulatedTime : currentTime

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      const startTime = performance.now()
      
      try {
        // Load starred sessions from localStorage
        const starred = getStarredSessions()
        setStarredSessions(starred)

        // Load schedule data with cache busting
        const timestamp = new Date().getTime()
        const response = await fetch(`/schedule_may30.json?v=${timestamp}`)
        if (!response.ok) {
          throw new Error(`Failed to load schedule: ${response.statusText}`)
        }
        const scheduleData = await response.json()
        setSessions(scheduleData)

        setLoading(false)
        
        // Track app load completion
        const loadTime = performance.now() - startTime
        analytics.appLoad(loadTime)
        
        // Track initial page view
        trackPageView('Editions Vibes - Conference Companion', {
          session_count: scheduleData.length,
          starred_count: starred.length,
          load_time: loadTime
        })

      } catch (err) {
        console.error('Failed to load app data:', err)
        setError(err.message)
        setLoading(false)
        
        // Track loading errors
        analytics.errorOccurred('data_load_error', err.message, 'app_initialization')
      }
    }

    loadData()
  }, [])

  // Update current time for real-time session status
  useEffect(() => {
    if (isSimulating) return

    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000) // Update every second for real-time countdown experience

    return () => clearInterval(interval)
  }, [isSimulating])

  // Debug panel keyboard shortcut (Ctrl+Shift+D)
  useEffect(() => {
    if (!isDevMode) return

    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault()
        setShowDebugPanel(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDevMode])

  // Handle starring/unstarring sessions with conflict detection
  const handleToggleStar = (sessionId) => {
    const isCurrentlyStarred = starredSessions.includes(sessionId)
    const session = sessions.find(s => s.id === sessionId)
    
    if (!isCurrentlyStarred) {
      // Adding a session - check for conflicts
      const newSession = sessions.find(s => s.id === sessionId)
      const currentlyStarredSessions = sessions.filter(s => starredSessions.includes(s.id))
      
      if (newSession) {
        const conflicts = findConflicts(newSession, currentlyStarredSessions)
        
        if (conflicts.length > 0) {
          const message = generateConflictMessage(conflicts)
          setToast({
            id: Date.now(),
            message,
            type: 'warning'
          })
          
          // Track schedule conflicts
          analytics.scheduleConflict('time_overlap', conflicts.length + 1)
        }
      }
    }

    // Proceed with starring/unstarring regardless of conflicts
    const updatedStarred = isCurrentlyStarred
      ? starredSessions.filter(id => id !== sessionId)
      : [...starredSessions, sessionId]

    setStarredSessions(updatedStarred)
    saveStarredSessions(updatedStarred)
    
    // Track starring/unstarring events
    if (session) {
      if (isCurrentlyStarred) {
        analytics.sessionUnstar(sessionId, session.title, session.category)
      } else {
        analytics.sessionStar(sessionId, session.title, session.category)
      }
    }
  }

  // Handle expanding/collapsing session details
  const handleToggleExpand = (sessionId) => {
    setExpandedId(expandedId === sessionId ? null : sessionId)
  }

  // Handle view switching
  const handleViewChange = (view) => {
    const previousView = currentView
    setCurrentView(view)
    setExpandedId(null) // Collapse any expanded session when switching views
    
    // Track last main view (non-My Plan view) for ViewSelector state preservation
    if (view !== 'my-plan') {
      setLastMainView(view)
    }
    
    // Track view changes
    analytics.viewChange(previousView, view)
    
    // Track page view for the new view
    const viewNames = {
      'agenda': 'Schedule View',
      'timeline': 'Timeline View', 
      'visual-timeline': 'Visual Timeline View',
      'my-plan': 'My Plan View'
    }
    trackPageView(viewNames[view] || view)
  }

  // Handle category filter changes
  const handleCategoryFilterChange = (filter) => {
    const previousFilter = categoryFilter
    setCategoryFilter(filter)
    setExpandedId(null) // Collapse any expanded session when filtering
    
    // Track filter usage
    const resultCount = filter === 'All' 
      ? sessions.length 
      : sessions.filter(s => s.category === filter).length
    analytics.filterApply('category', filter, resultCount)
  }

  // Handle content type filter changes
  const handleContentTypesChange = (contentTypes) => {
    setActiveContentTypes(contentTypes)
    setExpandedId(null) // Collapse any expanded session when filtering
    
    // Track content type filtering
    const resultCount = contentTypes.includes('__NONE_SELECTED__') 
      ? 0 
      : contentTypes.length === 0 
      ? sessions.length 
      : sessions.filter(s => contentTypes.includes(s.contentType)).length
    analytics.filterApply('content_type', contentTypes.join(','), resultCount)
  }

  // Handle calendar export completion
  const handleExportComplete = (result) => {
    setToast({
      id: Date.now(),
      message: result.message,
      type: result.success ? 'success' : 'warning'
    })
    
    // Track calendar exports
    if (result.success && result.sessionId) {
      const session = sessions.find(s => s.id === result.sessionId)
      if (session) {
        analytics.sessionCalendarExport(result.sessionId, session.title, result.method || 'unknown')
      }
    }
  }

  // Handle speaker click
  const handleSpeakerClick = (speaker) => {
    setSelectedSpeaker(speaker)
    setShowSpeakerModal(true)
    
    // Track speaker interactions
    const speakerSessions = sessions.filter(s => 
      s.speakers?.some(sp => sp.name === speaker.name)
    )
    analytics.speakerView(speaker.name, speakerSessions.length)
  }

  // Handle session click from dashboard or speaker modal
  const handleSessionClick = (sessionId) => {
    setExpandedId(sessionId)
    
    // Track session views
    const session = sessions.find(s => s.id === sessionId)
    if (session) {
      analytics.sessionView(sessionId, session.title, session.category, 'expanded')
    }
    
    // Scroll to session card after a brief delay for DOM update
    setTimeout(() => {
      const element = document.querySelector(`[data-session-id="${sessionId}"]`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  // Handle session click from timeline views (no expansion, just analytics)
  const handleTimelineSessionClick = (sessionId) => {
    // Track session views without expanding
    const session = sessions.find(s => s.id === sessionId)
    if (session) {
      analytics.sessionView(sessionId, session.title, session.category, 'timeline')
    }
  }

  // Close toast notification
  const handleCloseToast = () => {
    setToast(null)
  }

  // Close speaker modal
  const handleCloseSpeakerModal = () => {
    setShowSpeakerModal(false)
    setSelectedSpeaker(null)
  }

  // Close debug panel
  const handleCloseDebugPanel = () => {
    setShowDebugPanel(false)
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="w-10 h-10 border-3 border-editions-purple/30 border-t-editions-purple rounded-full animate-spin"></div>
        <p className="text-lg">Loading Editions Vibes...</p>
        {isDevMode && (
          <p className="text-xs text-editions-orange">Dev Mode Active - Press Ctrl+Shift+D for debug panel</p>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
        <h2 className="text-2xl font-bold">Oops! Something went wrong</h2>
        <p className="text-gray-300">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-editions-purple hover:bg-editions-purple-light rounded-lg font-medium transition-colors"
        >
          Retry
        </button>
        {isDevMode && (
          <button
            onClick={() => setShowDebugPanel(true)}
            className="px-4 py-2 bg-editions-orange/20 text-editions-orange border border-editions-orange/40 rounded-lg text-sm"
          >
            🛠️ Open Debug Panel
          </button>
        )}
      </div>
    )
  }

  // Filter sessions based on category and content type
  const categoryFilteredSessions = categoryFilter === 'All' 
    ? sessions 
    : sessions.filter(session => session.category === categoryFilter)

  // Handle content type filtering with special "__NONE_SELECTED__" marker
  const filteredSessions = activeContentTypes.includes('__NONE_SELECTED__')
    ? [] // Show no sessions when "Clear All" is clicked
    : activeContentTypes.length === 0
    ? categoryFilteredSessions // Show all when no specific content types selected
    : categoryFilteredSessions.filter(session => 
        activeContentTypes.includes(session.contentType)
      )

  const sortedSessions = [...filteredSessions].sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
  
  const starredSessionsData = sessions
    .filter(session => starredSessions.includes(session.id))
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))

  // Detect conflicts in starred sessions
  const conflictGroups = []
  starredSessionsData.forEach(session => {
    const conflicts = findConflicts(session, starredSessionsData.filter(s => s.id !== session.id))
    if (conflicts.length > 0) {
      // Check if this conflict group already exists
      const existingGroup = conflictGroups.find(group => 
        group.some(s => s.id === session.id) || 
        group.some(s => conflicts.some(c => c.id === s.id))
      )
      
      if (!existingGroup) {
        conflictGroups.push([session, ...conflicts])
      }
    }
  })

  return (
    <div className="max-w-4xl mx-auto min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-editions-dark/95 backdrop-blur-lg border-b border-editions-purple/30 p-3 relative">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="Editions Vibes Logo" 
              className="object-contain"
              style={{ 
                width: '110px',
                position: 'absolute',
                top: '-14px'
              }}
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            {/* View Selector (without My Plan) */}
            <ViewSelector
              currentView={currentView}
              onViewChange={handleViewChange}
              starredCount={0} // Remove starred count from view selector
              compact={true}
              excludeMyPlan={true} // Add flag to exclude My Plan
              lastMainView={lastMainView} // Pass last main view for state preservation
            />
            
            {/* Separate My Plan Button */}
            <button
              onClick={() => handleViewChange('my-plan')}
              className={`
                flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all duration-200 transform hover:scale-105
                ${currentView === 'my-plan'
                  ? 'bg-editions-orange/20 text-white border-editions-orange/40 shadow-md'
                  : 'bg-transparent text-gray-300 border-gray-600 hover:text-white hover:border-editions-orange/50 hover:bg-editions-orange/10'
                }
                ${starredSessions.length > 0 ? 'ring-1 ring-editions-orange/30' : ''}
                text-sm
              `}
              aria-label={`View my plan ${starredSessions.length > 0 ? `(${starredSessions.length} starred)` : ''}`}
            >
              <span className="text-sm">⭐</span>
              <span className="text-sm font-medium">
                My Plan
              </span>
              {starredSessions.length > 0 && (
                <span className="bg-editions-orange text-white text-xs px-1.5 py-0.5 rounded-full min-w-0">
                  {starredSessions.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Welcome Party Notice */}
      <AppGuide />

      {/* Mobile Enhancements */}
      <MobileEnhancements 
        effectiveTime={effectiveTime}
        isSimulating={isSimulating}
        currentSessions={sessions.filter(session => {
          const sessionStart = new Date(session.startTime)
          const sessionEnd = new Date(session.endTime)
          return effectiveTime >= sessionStart && effectiveTime <= sessionEnd
        })}
        upcomingSessions={sessions.filter(session => {
          const sessionStart = new Date(session.startTime)
          const timeDiff = sessionStart.getTime() - effectiveTime.getTime()
          return timeDiff > 0 && timeDiff <= 90 * 60 * 1000 // 90 minutes
        })}
      />

      {/* Main Content */}
      <main className="p-4">
        {currentView === 'agenda' ? (
          <div className="space-y-4">
            {/* Live Dashboard */}
            <LiveDashboard
              sessions={sessions}
              starredSessions={starredSessions}
              categoryFilter={categoryFilter}
              onSessionClick={handleSessionClick}
              currentTime={currentTime}
              isSimulating={isSimulating}
              simulatedTime={simulatedTime}
              setIsSimulating={setIsSimulating}
              setSimulatedTime={setSimulatedTime}
              effectiveTime={effectiveTime}
            />

            {/* Historical Context */}
            <HistoricalContext
              effectiveTime={effectiveTime}
              sessions={sessions}
              starredSessions={starredSessions}
              isDevMode={isDevMode}
            />

            {/* Category Filter */}
            <CategoryFilter
              activeFilter={categoryFilter}
              onFilterChange={handleCategoryFilterChange}
            />

            {/* Filter Tabs */}
            <FilterTabs
              sessions={sessions}
              activeContentTypes={activeContentTypes}
              onContentTypesChange={handleContentTypesChange}
            />

            {/* Session Cards */}
            {sortedSessions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🔍</div>
                <h3 className="text-lg font-semibold mb-2">No sessions found</h3>
                <p className="text-gray-400">
                  {activeContentTypes.includes('__NONE_SELECTED__') 
                    ? 'All content types have been cleared. Use "Select All" to show sessions again.'
                    : 'Try adjusting your category or content type filters.'
                  }
                </p>
              </div>
            ) : (
              sortedSessions.map(session => (
                <div key={session.id} data-session-id={session.id}>
                  <SessionCard
                    session={session}
                    isStarred={starredSessions.includes(session.id)}
                    isExpanded={expandedId === session.id}
                    onToggleStar={handleToggleStar}
                    onToggleExpand={handleToggleExpand}
                    onExportComplete={handleExportComplete}
                    onSpeakerClick={handleSpeakerClick}
                    currentTime={effectiveTime}
                  />
                </div>
              ))
            )}
          </div>
        ) : currentView === 'timeline' ? (
          <div className="space-y-4">
            {/* Category Filter */}
            <CategoryFilter
              activeFilter={categoryFilter}
              onFilterChange={handleCategoryFilterChange}
            />
            
            {/* Timeline View */}
            <TimelineView
              sessions={sessions}
              starredSessions={starredSessions}
              categoryFilter={categoryFilter}
              onSessionClick={handleTimelineSessionClick}
              onToggleStar={handleToggleStar}
              onExportComplete={handleExportComplete}
              currentTime={effectiveTime}
            />
          </div>
        ) : currentView === 'visual-timeline' ? (
          <div className="space-y-4">
            {/* Category Filter */}
            <CategoryFilter
              activeFilter={categoryFilter}
              onFilterChange={handleCategoryFilterChange}
            />
            
            {/* Visual Timeline View */}
            <VisualTimelineView
              sessions={sessions}
              starredSessions={starredSessions}
              categoryFilter={categoryFilter}
              onSessionClick={handleTimelineSessionClick}
              onToggleStar={handleToggleStar}
              onExportComplete={handleExportComplete}
              currentTime={effectiveTime}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {starredSessionsData.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                {/* Conflict Warning */}
                {conflictGroups.length > 0 && (
                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">⚠️</span>
                      <h3 className="text-white font-semibold">Schedule Conflicts Detected</h3>
                    </div>
                    <div className="space-y-2">
                      {conflictGroups.map((group, index) => (
                        <div key={index} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                          <div className="text-sm text-red-300 mb-2">
                            Conflict #{index + 1}: These sessions overlap in time
                          </div>
                          <div className="space-y-1">
                            {group.map(session => (
                              <div key={session.id} className="text-xs text-gray-300 flex items-center gap-2">
                                <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                                <span className="font-medium">{session.title}</span>
                                <span className="text-gray-400">
                                  ({formatTime(session.startTime)} - {formatTime(session.endTime)})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-xs text-gray-400">
                      You can keep conflicting sessions in your plan, but you'll need to choose which ones to attend.
                    </div>
                  </div>
                )}

                {/* My Plan Header with Export Button */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-editions-purple/30">
                  <div>
                    <h2 className="text-xl font-semibold text-white">Your Personal Schedule</h2>
                    <p className="text-gray-400 text-sm mt-1">
                      {starredSessionsData.length} session{starredSessionsData.length !== 1 ? 's' : ''} in your plan
                      {conflictGroups.length > 0 && (
                        <span className="text-red-400 ml-2">
                          • {conflictGroups.length} conflict{conflictGroups.length !== 1 ? 's' : ''} detected
                        </span>
                      )}
                    </p>
                  </div>
                  <ExportButton 
                    sessions={starredSessionsData}
                    onExportComplete={handleExportComplete}
                  />
                </div>

                {/* Starred Sessions */}
                {starredSessionsData.map(session => {
                  // Check if this session is in a conflict
                  const isInConflict = conflictGroups.some(group => 
                    group.some(s => s.id === session.id)
                  )

                  return (
                    <div key={session.id} data-session-id={session.id}>
                      <SessionCard
                        session={session}
                        isStarred={true}
                        isExpanded={expandedId === session.id}
                        isInConflict={isInConflict}
                        onToggleStar={handleToggleStar}
                        onToggleExpand={handleToggleExpand}
                        onExportComplete={handleExportComplete}
                        onSpeakerClick={handleSpeakerClick}
                        currentTime={effectiveTime}
                      />
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}
      </main>

      {/* Speaker Modal */}
      <SpeakerModal
        speaker={selectedSpeaker}
        sessions={sessions}
        isOpen={showSpeakerModal}
        onClose={handleCloseSpeakerModal}
        onSessionClick={handleSessionClick}
        onExportComplete={handleExportComplete}
      />

      {/* Debug Panel */}
      {isDevMode && showDebugPanel && (
        <DebugPanel onClose={handleCloseDebugPanel} />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  )
}